// src/transactions/transactionSave.js
import { db } from "../../firebase/firebase";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  runTransaction,
} from "firebase/firestore";

import { uploadProofFile } from "../../firebase/uploadHelper";

const normalizeName = (name) =>
  (name || "").toLowerCase().replace(/\s+/g, " ").trim();

const isCashAccountEligibleForVisaMirror = (accountName) => {
  const normalized = normalizeName(accountName);
  return (
    normalized.includes("cash in hand - comfort line") ||
    normalized.includes("cash in hand - lolia limousine")
  );
};

const getCompanyNameForMirror = (accountName) =>
  normalizeName(accountName).includes("comfort line")
    ? "Comfort Line"
    : "Lolia Limousine";

const getSavedDate = (date) =>
  typeof date === "string"
    ? date
    : new Date().toISOString().split("T")[0];

export default async function saveTransaction(values) {
  const BULK_MODE = window.__BULK_SAVE_MODE__ === true;

  const {
    accountId,
    amountIn = 0,
    amountOut = 0,
    date,
    expenseType,
    description,
    paidBy,
    comment,
    proofFile,
    isMirror,
    id,
  } = values;

  try {
    const accountDocId =
      typeof accountId === "string" ? accountId : accountId?.id;

    if (!accountDocId) throw new Error("Account ID missing");

    const accountRef = doc(db, "accounts", accountDocId);
    const accSnap = await getDoc(accountRef);
    if (!accSnap.exists()) throw new Error("Account not found");

    const accData = accSnap.data();

    let proofUrl = "";
    if (proofFile && proofFile.rawFile) {
      proofUrl = await uploadProofFile(proofFile.rawFile);
    }

    // ---------------------------------------------------
    // EDIT MODE — KEEP ORIGINAL + VISA MIRROR IN SYNC
    // ---------------------------------------------------
    if (id) {
      console.log("✏️ Editing existing transaction:", id);

      const txRef = doc(db, "transactions", id);
      const existingSnap = await getDoc(txRef);
      if (!existingSnap.exists()) throw new Error("Transaction not found");

      const existing = existingSnap.data();
      const oldAccountId = existing.accountId;
      if (!oldAccountId) throw new Error("Existing transaction account is missing");

      // Find a mirror linked to this exact original transaction. Querying only
      // originalTransactionId avoids a composite-index dependency.
      const mirrorQuery = query(
        collection(db, "transactions"),
        where("originalTransactionId", "==", id)
      );
      const mirrorSnap = await getDocs(mirrorQuery);
      const linkedMirrors = mirrorSnap.docs.filter(
        (mirrorDoc) => mirrorDoc.data()?.isMirror === true
      );

      if (linkedMirrors.length > 1) {
        throw new Error(
          "Multiple Visa mirror entries are linked to this transaction. Please review them before editing."
        );
      }

      const existingMirrorDoc = linkedMirrors[0] || null;
      const existingMirrorData = existingMirrorDoc?.data() || null;

      const newAccountName = accData.name || "";
      const shouldMirrorAfterEdit =
        existing.isMirror !== true &&
        normalizeName(paidBy) === "bank visa card" &&
        isCashAccountEligibleForVisaMirror(newAccountName);

      let visaAccountRef = null;

      if (existingMirrorData?.accountId) {
        visaAccountRef = doc(db, "accounts", existingMirrorData.accountId);
      } else if (shouldMirrorAfterEdit) {
        const visaQuery = query(
          collection(db, "accounts"),
          where("name", "==", "Visa Card")
        );
        const visaSnap = await getDocs(visaQuery);

        if (visaSnap.empty) {
          throw new Error(
            "Visa Card account not found. Transaction was not updated because the required mirror entry could not be created."
          );
        }

        visaAccountRef = doc(db, "accounts", visaSnap.docs[0].id);
      }

      const newMirrorRef =
        shouldMirrorAfterEdit && !existingMirrorDoc
          ? doc(collection(db, "transactions"))
          : null;

      const oldAccountRef = doc(db, "accounts", oldAccountId);

      await runTransaction(db, async (firestoreTx) => {
        // All Firestore reads must happen before any writes.
        const freshOriginalSnap = await firestoreTx.get(txRef);
        if (!freshOriginalSnap.exists()) throw new Error("Transaction not found");
        const freshOriginal = freshOriginalSnap.data();

        const freshOldAccountSnap = await firestoreTx.get(oldAccountRef);
        if (!freshOldAccountSnap.exists()) {
          throw new Error("Original account not found");
        }
        const freshOldAccount = freshOldAccountSnap.data();

        let freshNewAccountSnap = freshOldAccountSnap;
        let freshNewAccount = freshOldAccount;

        if (oldAccountId !== accountDocId) {
          freshNewAccountSnap = await firestoreTx.get(accountRef);
          if (!freshNewAccountSnap.exists()) throw new Error("New account not found");
          freshNewAccount = freshNewAccountSnap.data();
        }

        let freshMirror = null;
        if (existingMirrorDoc) {
          const freshMirrorSnap = await firestoreTx.get(existingMirrorDoc.ref);
          if (!freshMirrorSnap.exists()) {
            throw new Error("Linked Visa mirror transaction not found");
          }
          freshMirror = freshMirrorSnap.data();
        }

        let freshVisaAccount = null;
        if (visaAccountRef) {
          const freshVisaSnap = await firestoreTx.get(visaAccountRef);
          if (!freshVisaSnap.exists()) throw new Error("Visa Card account not found");
          freshVisaAccount = freshVisaSnap.data();
        }

        const oldAmountIn = Number(freshOriginal.amountIn || 0);
        const oldAmountOut = Number(freshOriginal.amountOut || 0);
        const newAmountIn = Number(amountIn || 0);
        const newAmountOut = Number(amountOut || 0);

        const oldCurrentBalance =
          typeof freshOldAccount.currentBalance === "number"
            ? freshOldAccount.currentBalance
            : Number(freshOldAccount.openingBalance || 0);

        if (oldAccountId === accountDocId) {
          const adjustedBalance =
            oldCurrentBalance +
            (newAmountIn - oldAmountIn) -
            (newAmountOut - oldAmountOut);

          firestoreTx.update(accountRef, { currentBalance: adjustedBalance });
        } else {
          const restoredOldBalance =
            oldCurrentBalance - oldAmountIn + oldAmountOut;

          const newCurrentBalance =
            typeof freshNewAccount.currentBalance === "number"
              ? freshNewAccount.currentBalance
              : Number(freshNewAccount.openingBalance || 0);
          const adjustedNewBalance =
            newCurrentBalance + newAmountIn - newAmountOut;

          firestoreTx.update(oldAccountRef, {
            currentBalance: restoredOldBalance,
          });
          firestoreTx.update(accountRef, {
            currentBalance: adjustedNewBalance,
          });
        }

        const savedDate = getSavedDate(date);

        firestoreTx.update(txRef, {
          accountId: accountDocId,
          accountName: freshNewAccount.name || newAccountName,
          date: savedDate,
          expenseType: expenseType || "",
          description: description || "",
          amountIn: newAmountIn,
          amountOut: newAmountOut,
          paidBy: paidBy || "",
          comment: comment || "",
          proofUrl: proofUrl || freshOriginal.proofUrl || "",
          updatedAt: new Date(),
        });

        // Reverse the existing mirror effect first, then apply the new mirror
        // effect if the edited transaction still qualifies.
        if (visaAccountRef && freshVisaAccount) {
          const visaCurrentBalance =
            typeof freshVisaAccount.currentBalance === "number"
              ? freshVisaAccount.currentBalance
              : Number(freshVisaAccount.openingBalance || 0);

          const oldMirrorAmountOut = Number(freshMirror?.amountOut || 0);
          const newMirrorAmountOut = shouldMirrorAfterEdit ? newAmountOut : 0;
          const adjustedVisaBalance =
            visaCurrentBalance + oldMirrorAmountOut - newMirrorAmountOut;

          firestoreTx.update(visaAccountRef, {
            currentBalance: adjustedVisaBalance,
          });

          if (shouldMirrorAfterEdit) {
            const companyName = getCompanyNameForMirror(
              freshNewAccount.name || newAccountName
            );
            const mirrorDescription =
              `MIRROR → Original: ${description || expenseType || "No Description"} ` +
              `| Account: ${freshNewAccount.name || newAccountName}`;

            const mirrorData = {
              accountId: visaAccountRef.id,
              accountName: freshVisaAccount.name || "Visa Card",
              date: savedDate,
              expenseType: "Auto Mirror",
              description: mirrorDescription,
              amountIn: 0,
              amountOut: newAmountOut,
              balance: adjustedVisaBalance,
              paidBy: companyName,
              originalTransactionId: id,
              comment: `Auto-generated mirror entry from ${freshNewAccount.name || newAccountName}`,
              proofUrl: proofUrl || freshOriginal.proofUrl || "",
              isMirror: true,
            };

            if (existingMirrorDoc) {
              firestoreTx.update(existingMirrorDoc.ref, {
                ...mirrorData,
                updatedAt: new Date(),
              });
            } else {
              firestoreTx.set(newMirrorRef, {
                ...mirrorData,
                createdAt: new Date(),
              });
            }
          } else if (existingMirrorDoc) {
            firestoreTx.delete(existingMirrorDoc.ref);
          }
        }
      });

      if (!BULK_MODE) alert(`✅ Transaction updated for ${accData.name}`);

      return { success: true, mode: "edit", id };
    }

    // ---------------------------------------------------
    // CREATE MODE — ATOMIC BALANCE + MIRROR WRITE
    // ---------------------------------------------------
    // For new transactions, keep the transaction document, account balance,
    // Visa mirror document, and Visa balance in one Firestore transaction.
    // This is especially important for large Excel/bulk entries: either the
    // accounting writes for one row all succeed together, or none are posted.

    const accountNameNorm = normalizeName(accData.name);
    const shouldMirror =
      !isMirror &&
      typeof paidBy === "string" &&
      normalizeName(paidBy) === "bank visa card" &&
      isCashAccountEligibleForVisaMirror(accData.name);

    let visaAccountRef = null;
    if (shouldMirror) {
      const visaQuery = query(
        collection(db, "accounts"),
        where("name", "==", "Visa Card")
      );
      const visaSnap = await getDocs(visaQuery);

      if (visaSnap.empty) {
        throw new Error(
          "Visa Card account not found. Transaction was not posted because the required mirror entry could not be created."
        );
      }

      visaAccountRef = doc(db, "accounts", visaSnap.docs[0].id);
    }

    const txRef = doc(collection(db, "transactions"));
    const mirrorTxRef = shouldMirror
      ? doc(collection(db, "transactions"))
      : null;

    await runTransaction(db, async (firestoreTx) => {
      // Firestore requires transaction reads before writes.
      const freshAccountSnap = await firestoreTx.get(accountRef);
      if (!freshAccountSnap.exists()) throw new Error("Account not found");

      const freshAccData = freshAccountSnap.data();
      const freshOldBalance =
        typeof freshAccData.currentBalance === "number"
          ? freshAccData.currentBalance
          : freshAccData.openingBalance || 0;
      const freshNewBalance =
        freshOldBalance +
        (Number(amountIn) || 0) -
        (Number(amountOut) || 0);

      let visaData = null;
      let newVisaBalance = null;

      if (shouldMirror) {
        const freshVisaSnap = await firestoreTx.get(visaAccountRef);
        if (!freshVisaSnap.exists()) {
          throw new Error("Visa Card account not found");
        }

        visaData = freshVisaSnap.data();
        const oldVisaBalance =
          typeof visaData.currentBalance === "number"
            ? visaData.currentBalance
            : visaData.openingBalance || 0;

        newVisaBalance = oldVisaBalance - Number(amountOut || 0);
      }

      const savedDate = getSavedDate(date);

      firestoreTx.set(txRef, {
        accountId: accountDocId,
        accountName: freshAccData.name || "",
        date: savedDate,
        expenseType: expenseType || "",
        description: description || "",
        amountIn: Number(amountIn) || 0,
        amountOut: Number(amountOut) || 0,
        balance: freshNewBalance,
        paidBy: paidBy || "",
        comment: comment || "",
        proofUrl: proofUrl || "",
        createdAt: new Date(),
        isMirror: !!isMirror,
      });

      firestoreTx.update(accountRef, { currentBalance: freshNewBalance });

      if (shouldMirror) {
        const companyName = accountNameNorm.includes("comfort line")
          ? "Comfort Line"
          : "Lolia Limousine";

        const mirrorDescription =
          `MIRROR → Original: ${description || expenseType || "No Description"} ` +
          `| Account: ${freshAccData.name || accData.name || ""}`;

        firestoreTx.set(mirrorTxRef, {
          accountId: visaAccountRef.id,
          accountName: visaData?.name || "Visa Card",
          date: savedDate,
          expenseType: "Auto Mirror",
          description: mirrorDescription,
          amountIn: 0,
          amountOut: Number(amountOut) || 0,
          balance: newVisaBalance,
          paidBy: companyName,
          originalTransactionId: txRef.id,
          comment: `Auto-generated mirror entry from ${freshAccData.name || accData.name || ""}`,
          proofUrl: proofUrl || "",
          createdAt: new Date(),
          isMirror: true,
        });

        firestoreTx.update(visaAccountRef, {
          currentBalance: newVisaBalance,
        });
      }
    });

    console.log("✅ Transaction created atomically:", txRef.id);
    if (shouldMirror) {
      console.log("✅ Visa mirror and both balances committed atomically");
    }

    if (!BULK_MODE) alert(`✅ Transaction saved for ${accData.name}`);

    return { success: true, mode: "create", id: txRef.id };
  } catch (err) {
    console.error("❌ Save Transaction Error:", err);
    if (!BULK_MODE) alert("Error: " + err.message);
    return { success: false, error: err.message };
  }
}
